import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { SteppedContent } from './SteppedContent'
import cls from './stepped-form.module.scss'
import { FormElementsGroup } from '@/shared/ui/forms/FormElementsGroup/FormElementsGroup'
import RoundedButton from '@/shared/ui/buttons/RoundedButton/RoundedButton'

const getVisibleSteps = (steps = []) => steps.filter(step => !step.hidden)

export const SteppedForm = props => {
  const [externalErrorStep, setExternalErrorStep] = useState(undefined)
  const [formValues, setFormValues] = useState({})

  const elementsRef = useRef(null)
  const previousExternalErrorsRef = useRef(undefined)

  const {
    classes: externalClasses = {},
    steps = [],
    isSubmitting,
    buttonsAlign,
    externalErrors,
    onChangeField,
    onSubmit,
    onNextStep,
    width: size = '',
    activeStepIndex,
    setActiveStepIndex,
    hideStepsIndicator = false,
  } = props

  const visibleSteps = useMemo(() => getVisibleSteps(steps), [steps])

  useEffect(() => {
    setFormValues(prev => {
      const next = {}

      steps.forEach(step => {
        next[step.id] = prev[step.id] || {}
      })

      return next
    })
  }, [steps])

  useEffect(() => {
    if (externalErrors === previousExternalErrorsRef.current) {
      setExternalErrorStep(undefined)
      return
    }

    previousExternalErrorsRef.current = externalErrors

    if (externalErrors === undefined) {
      setExternalErrorStep(undefined)
      return
    }

    let nextExternalErrorStep

    visibleSteps.forEach((step, index) => {
      ;(step.elements || []).forEach(element => {
        if (externalErrors[element.id]) {
          nextExternalErrorStep = index
        }
      })
    })

    if (nextExternalErrorStep !== undefined) {
      setActiveStepIndex(nextExternalErrorStep)
      setExternalErrorStep(nextExternalErrorStep)
      return
    }

    setExternalErrorStep(undefined)
  }, [externalErrors, visibleSteps])

  const setElementsRef = useCallback(ref => {
    elementsRef.current = ref
  }, [])

  const getCurrentFormValues = useCallback(() => {
    const currentStep = visibleSteps[activeStepIndex]
    const nextFormValues = { ...formValues }
    const currentFieldValues = elementsRef.current && elementsRef.current.getValues()

    if (currentStep) {
      nextFormValues[currentStep.id] = currentFieldValues
    }

    return nextFormValues
  }, [activeStepIndex, formValues, visibleSteps])

  const gotoStep = useCallback(
    index => {
      const currentValues = getCurrentFormValues()

      setActiveStepIndex(index)
      setFormValues(currentValues)
    },
    [getCurrentFormValues],
  )

  const handleStepClick = useCallback(index => gotoStep(index), [gotoStep])

  const handleBackClick = useCallback(() => {
    gotoStep(activeStepIndex - 1)
  }, [activeStepIndex, gotoStep])

  const handleNextClick = useCallback(
    async event => {
      event && event.preventDefault()
      if (isSubmitting) return

      const isValid = elementsRef.current ? elementsRef.current.checkValidity() : true
      if (!isValid) return

      const currentStep = visibleSteps[activeStepIndex]
      if (!currentStep) return

      const stepId = currentStep.id
      const currentValues = getCurrentFormValues()

      if (activeStepIndex >= visibleSteps.length - 1) {
        onSubmit && onSubmit(currentValues, stepId)
        return
      }

      if (onNextStep) {
        const isNextValid = await onNextStep(currentValues[stepId], stepId)
        if (!isNextValid) return
      }

      setFormValues(currentValues)
      setActiveStepIndex(prev => prev + 1)
    },
    [activeStepIndex, getCurrentFormValues, isSubmitting, onNextStep, onSubmit, visibleSteps],
  )

  return (
    <SteppedContent
      steps={visibleSteps}
      activeStepIndex={externalErrorStep ?? activeStepIndex}
      onStepClick={handleStepClick}
      hideStepsIndicator={hideStepsIndicator}
    >
      {visibleSteps.map(step => {
        const { extraComponent } = step
        return (
          <form
            key={step.id}
            noValidate
            className={classNames(cls.main, externalClasses.form)}
            onSubmit={handleNextClick}
          >
            {step.elements && (
              <div className={cls.formContainer}>
                <FormElementsGroup
                  size={size}
                  externalErrors={externalErrorStep !== undefined && externalErrors}
                  elements={step.elements}
                  onChangeField={onChangeField}
                  values={formValues[step.id]}
                  ref={setElementsRef}
                />
              </div>
            )}
            <div
              className={classNames(cls.infoContainer, {
                [cls.reverseContainer]: extraComponent?.position === 'before',
              })}
            >
              <RoundedButton
                type="submit"
                loading={isSubmitting}
                key="submitButton"
                variant="DARK"
                id={step.nextButtonId}
                className={classNames(cls.button, externalClasses.button)}
              >
                {step.nextButtonLabel}
              </RoundedButton>
              {extraComponent?.component}
            </div>
          </form>
        )
      })}
    </SteppedContent>
  )
}

SteppedForm.propTypes = {
  buttonsAlign: PropTypes.oneOf(['left', 'center', 'right']),
  classes: PropTypes.shape({
    form: PropTypes.string,
    buttonsContainer: PropTypes.string,
    button: PropTypes.string,
  }),
  hideStepsIndicator: PropTypes.bool,
}

SteppedForm.defaultProps = {
  buttonsAlign: 'center',
  classes: {},
}
